import { tr } from "../../i18n";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../auth/AuthContext";
import { Badge, Card, fmtDateTime } from "../../components/portal";
export default function AdminEriDetail() {
  const {
    lang = "en",
    id
  } = useParams();
  const [doc, setDoc] = useState(null);
  const [clients, setClients] = useState([]);
  const [linkId, setLinkId] = useState("");
  const load = () => api.get(`/admin/eri-submissions/${id}`).then(r => setDoc(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/clients/simple").then(r => setClients(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (!doc) return <p className="text-sm text-esaia-subtle">{tr("Loading...")}</p>;
  const bandTone = doc.band === "Established" ? "green" : doc.band === "Developing" ? "amber" : "red";
  const linkClient = async () => {
    try {
      await api.patch(`/admin/eri-submissions/${id}`, {
        client_id: linkId || null
      });
      toast.success(tr("Submission updated."));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const info = [["First name", doc.first_name], ["Last name", doc.last_name], ["Company", doc.company], ["Email", doc.email], ["Date completed", fmtDateTime(doc.created_at)]];
  return <div className="mx-auto max-w-5xl" data-testid="admin-eri-detail">
      <Link to={`/${lang}/admin/eri`} className="group inline-flex items-center gap-2 text-xs font-semibold text-esaia-blue">
        <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" />{" "}{tr("Back to ERI Submissions")}{" "}</Link>

      <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6">
          <Card title={tr("Contact Information")} testid="eri-contact-info">
            <dl className="space-y-4">
              {info.map(([k, v]) => <div key={k}>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-subtle">{tr(k)}</dt>
                  <dd className="mt-1 text-sm font-medium text-esaia-ink">{v || "—"}</dd>
                </div>)}
            </dl>
          </Card>

          <Card title={tr("Client Link")} testid="eri-client-link">
            {doc.linked_client ? <p className="text-sm text-esaia-ink">{tr("Linked to")}{" "}
                <Link to={`/${lang}/admin/clients/${doc.linked_client.id}`} className="font-semibold text-esaia-blue hover:underline">
                  {doc.linked_client.company || `${doc.linked_client.first_name} ${doc.linked_client.last_name}`}
                </Link>
              </p> : <p className="text-sm text-esaia-subtle">{tr("Not linked to a client account.")}</p>}
            <select value={linkId} onChange={e => setLinkId(e.target.value)} data-testid="eri-link-select" className="mt-4 w-full border border-esaia-line bg-white px-3 py-2.5 text-sm text-esaia-ink focus:border-esaia-blue focus:outline-none">
              <option value="">{tr("Select client...")}</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.company ? `${c.company} (${c.name})` : `${c.name} (${c.email})`}</option>)}
            </select>
            <button onClick={linkClient} data-testid="eri-link-save" className="mt-3 w-full bg-esaia-blue px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark">
              {doc.linked_client ? tr("Update Link") : tr("Link to Client")}
            </button>
          </Card>
        </div>

        <div className="space-y-6 lg:col-span-2">
          <Card title={tr("ERI Results")} testid="eri-results">
            <div className="flex flex-wrap items-center gap-5">
              <p className="font-serif text-5xl font-light text-esaia-blue">{doc.total_score}<span className="text-xl text-esaia-subtle">/100</span></p>
              <Badge tone={bandTone}>{tr(doc.band || "—")}</Badge>
            </div>
            <div className="mt-6 space-y-4">
              {Object.entries(doc.section_scores || {}).map(([k, v]) => <div key={k}>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-esaia-ink">{tr(k)}</span>
                    <span className="font-mono text-xs text-esaia-subtle">{v}%</span>
                  </div>
                  <div className="mt-1.5 h-2 w-full bg-esaia-line">
                    <div className={`h-2 ${v >= 70 ? "bg-emerald-600" : v >= 40 ? "bg-amber-500" : "bg-red-500"}`} style={{
                  width: `${v}%`
                }} />
                  </div>
                </div>)}
            </div>
            {(doc.attention || []).length > 0 && <div className="mt-6 border-t border-esaia-line pt-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-red-600">{tr("Risk indicators")}</p>
                <ul className="mt-3 space-y-2.5">
                  {doc.attention.map((a, i) => <li key={i} className="flex items-start gap-3 text-sm text-esaia-muted">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-red-500" />
                      <span><span className="font-medium text-esaia-ink">{a.section}:</span> {a.text}</span>
                    </li>)}
                </ul>
              </div>}
          </Card>

          <Card title={tr("Answers")} testid="eri-answers">
            {(doc.detailed_answers || []).length === 0 ? <p className="text-sm text-esaia-subtle">{tr("Detailed answers are available for submissions completed after the portal launch.")}</p> : <ol className="space-y-5">
                {doc.detailed_answers.map((a, i) => <li key={i} className="border-b border-esaia-line pb-5 last:border-0 last:pb-0" data-testid={`eri-answer-${a.n}`}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-subtle">{tr("Question")}{" "}{a.n} · {tr(a.section)}</p>
                    <p className="mt-1.5 text-sm font-medium text-esaia-ink">{tr(a.question)}</p>
                    <p className="mt-1.5 text-sm text-esaia-muted">{tr("Answer:")}{" "}<span className="font-semibold text-esaia-blue">{tr(a.answer)}</span>
                    </p>
                  </li>)}
              </ol>}
          </Card>
        </div>
      </div>
    </div>;
}
